import React from 'react';
import { RenderRequest } from 'types/cx-editor';
import { Box, Typography, useTheme, Card, CardHeader } from '@mui/material';
import { useWidgetSdk } from 'context/WidgetSdkContext';
import { WidgetErrorBoundary } from './WidgetErrorBoundary';
import { initializeAndRenderWidget } from 'utils/widget-renderer';
import { getWidgetIdFromTemplate } from 'constants/web-widget-mapping';
import { useSelector } from 'react-redux';
import { RootState } from 'store/Store';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { selectScenariosForTemplate, selectDataScenariosLoading } from 'store/dataReducers/dataScenariosSlice';
import { DataScenario } from 'types/cx-editor';
import { toast } from 'utils/toast';

type VariantConfig = {
  templateKey?: string;
  hierarchy?: string;
  locale?: string;
  scenario?: string;
};

type PreviewFrameProps = {
  variantName?: string;
  variant?: VariantConfig | null;
  renderRequest: RenderRequest | null;
  previewMode: 'desktop' | 'mobile';
  channel?: string;
  isFullTemplateSelected?: boolean;
  renderedHtml?: string;
  renderedSubject?: string;
  loading?: boolean;
  isWebWidgetTemplate?: boolean;
};

export const PreviewFrame: React.FC<PreviewFrameProps> = ({
  variantName,
  variant,
  renderRequest,
  previewMode,
  channel = 'Email',
  isFullTemplateSelected = false,
  renderedHtml,
  renderedSubject,
  loading = false,
  isWebWidgetTemplate = false,
}) => {
  const theme = useTheme();
  const effectiveTemplateKey = variant?.templateKey || renderRequest?.templateKey;
  const refreshKey = renderRequest?.refreshKey ?? null; // Extract refreshKey to trigger re-render on save
  const effectiveElementKey = renderRequest?.elementKey || null;
  const { instanceId, isInitializing, initError, isReady, reinitialize } = useWidgetSdk();

  const widgetContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [selectedWidgetTag, setSelectedWidgetTag] = React.useState<string | null>(null);
  const errorToastShownRef = React.useRef<string | null>(null); // Track which template we've shown error for
  
  // Track template changes, refreshKey, and scenario changes to trigger re-initialization
  const prevTemplateKeyRef = React.useRef<string | null>(null);
  const prevRefreshKeyRef = React.useRef<number | null>(null);
  const prevScenarioIdRef = React.useRef<string | null>(null);

  // Get data from Redux
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const projectAttributes = useSelector(selectProjectAttributes);
  
  // Extract fuelType from projectAttributes
  const fuelType = React.useMemo(() => {
    let fuelType = 'ELECTRIC'; // Default fallback
    if (projectAttributes?.fuel_type) {
      if (typeof projectAttributes.fuel_type === 'string') {
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    return fuelType;
  }, [projectAttributes]);

  const locale = variant?.locale || renderRequest?.variant?.locale || 'en_US';

  // Get scenarios from Redux
  const availableScenarios = useSelector((state: RootState) => {
    if (!effectiveTemplateKey || !selectedPilotId) {
      return [];
    }
    const scenarios = selectScenariosForTemplate(
      state,
      String(selectedPilotId),
      fuelType,
      locale,
      effectiveTemplateKey,
      channel
    );
    
    // Debug logging for Web channel
    if (channel === 'Web') {
      console.log('[PreviewFrame] Getting scenarios for Web:', {
        effectiveTemplateKey,
        channel,
        fuelType,
        locale,
        selectedPilotId,
        scenariosCount: scenarios.length,
        scenarios: scenarios.map(ds => ({
          templateName: ds.templateName,
          productType: ds.productType,
          scenarioId: ds.scenarioId
        }))
      });
    }
    
    return scenarios;
  });

  // Resolve scenario ID: always return an actual scenarioId from DataScenario objects
  // Note: 'default' is just a display name, not a scenarioId
  // availableScenarios are already filtered by template and productType (channel) in the selector
  const resolvedScenarioId = React.useMemo(() => {
    const scenario = variant?.scenario || renderRequest?.variant?.scenario;
    
    // If no scenarios available, we cannot resolve a scenarioId
    if (!availableScenarios || availableScenarios.length === 0) {
      console.error('[PreviewFrame] No scenarios available - cannot resolve scenarioId');
      return null; // Return null instead of 'default' string
    }
    
    // If a specific scenario is provided, try to find it in availableScenarios
    if (scenario && scenario !== 'default') {
      const foundScenario = availableScenarios.find(ds => ds.scenarioId === scenario);
      if (foundScenario) {
        // Verify productType matches channel (availableScenarios should already be filtered, but double-check)
        const channelLower = (channel || 'Email').toLowerCase();
        if (foundScenario.productType?.toLowerCase() === channelLower) {
          return foundScenario.scenarioId;
        }
        console.warn('[PreviewFrame] Scenario found but productType mismatch:', {
          scenarioId: foundScenario.scenarioId,
          productType: foundScenario.productType,
          expectedChannel: channel
        });
        // Fall through to find default scenario
      } else {
        console.warn('[PreviewFrame] Scenario not found in availableScenarios:', scenario);
        // Fall through to find default scenario
      }
    }
    
    // Find default scenario: first try defaultScenario flag, then scenarioDisplayName "Default", then first scenario
    // This handles both "default" string and when no scenario is provided
    const defaultScenario = availableScenarios.find(ds => ds.defaultScenario === true) ||
                           availableScenarios.find(ds => ds.scenarioDisplayName === 'Default') ||
                           availableScenarios[0];
    
    if (defaultScenario) {
      return defaultScenario.scenarioId; // Always return actual scenarioId, never 'default' string
    }
    
    // This should never happen if availableScenarios has items
    console.error('[PreviewFrame] Could not find any scenario in availableScenarios');
    return null;
  }, [variant?.scenario, renderRequest?.variant?.scenario, availableScenarios, channel]);

  // Get loading state to avoid showing error while scenarios are still loading
  const scenariosLoading = useSelector((state: RootState) => {
    if (!selectedPilotId) return false;
    return selectDataScenariosLoading(
      state,
      String(selectedPilotId),
      fuelType,
      locale
    );
  });

  // Show error toast when no data scenario is found for Web widget
  // Use a delayed approach to avoid showing toast during rapid state changes
  React.useEffect(() => {
    // Only show error for Web widgets when:
    // 1. Template is selected
    // 2. Scenarios are not loading
    // 3. SDK is ready (to avoid showing during initial load)
    // 4. No scenarios available
    if (
      channel === 'Web' &&
      isWebWidgetTemplate &&
      effectiveTemplateKey &&
      !scenariosLoading &&
      isReady && // Only show when SDK is ready
      (!availableScenarios || availableScenarios.length === 0)
    ) {
      // Only show toast once per template to avoid spam
      if (errorToastShownRef.current !== effectiveTemplateKey) {
        // Add a delay to avoid showing toast during rapid state changes or initial load
        // This ensures we only show the error when the user is actually trying to view the widget
        const timeoutId = setTimeout(() => {
          // Double-check conditions after delay to ensure we still need to show the error
          if (
            channel === 'Web' &&
            isWebWidgetTemplate &&
            effectiveTemplateKey &&
            !scenariosLoading &&
            isReady &&
            (!availableScenarios || availableScenarios.length === 0) &&
            errorToastShownRef.current !== effectiveTemplateKey
          ) {
        errorToastShownRef.current = effectiveTemplateKey;
            // Use warning instead of error to be less aggressive
            toast.warning(
              `No data scenario found for widget "${effectiveTemplateKey}". Please configure a data scenario in the Data Scenarios section.`
        );
          }
        }, 2000); // Wait 2 seconds before showing toast to ensure stable state

        return () => {
          clearTimeout(timeoutId);
        };
      }
    } else if (availableScenarios && availableScenarios.length > 0) {
      // Reset the ref when scenarios become available
      errorToastShownRef.current = null;
    }
  }, [
    channel,
    isWebWidgetTemplate,
    effectiveTemplateKey,
    availableScenarios,
    scenariosLoading,
    isReady, // Add isReady to dependencies
  ]);

  // Render widget when SDK is ready and template is selected
  React.useEffect(() => {
    if (channel !== 'Web' || !isWebWidgetTemplate) {
      setSelectedWidgetTag(null);
      if (widgetContainerRef.current) {
        widgetContainerRef.current.innerHTML = '';
      }
      return;
    }

    if (!effectiveTemplateKey || !isReady || !instanceId || !resolvedScenarioId) {
      return;
    }

    // Use unified widget renderer utility
    // Re-initialize on template change (menu item click), refreshKey change (after save), OR scenario change (dropdown)
    const templateChanged = effectiveTemplateKey !== prevTemplateKeyRef.current && prevTemplateKeyRef.current !== null;
    const refreshKeyChanged = refreshKey !== null && refreshKey !== prevRefreshKeyRef.current && prevRefreshKeyRef.current !== null;
    const scenarioChanged = resolvedScenarioId !== prevScenarioIdRef.current && prevScenarioIdRef.current !== null;
    const shouldReinitialize = templateChanged || refreshKeyChanged || scenarioChanged; // Re-initialize on template change, save, or scenario change

    initializeAndRenderWidget({
      templateKey: effectiveTemplateKey,
      scenarioId: resolvedScenarioId,
      instanceId: instanceId,
      containerRef: widgetContainerRef,
      reinitialize: shouldReinitialize ? reinitialize : undefined, // Re-initialize on template change, save, or scenario change
      onRenderComplete: (response) => {
        // Get widget tag for state tracking
        const widgetKey = getWidgetIdFromTemplate(effectiveTemplateKey);
        if (widgetKey && window.BidgelyWebSdk?.WIDGET_TAG?.[widgetKey]) {
          setSelectedWidgetTag(window.BidgelyWebSdk.WIDGET_TAG[widgetKey]);
        }
        console.log('[PreviewFrame] Web widget render response:', response);
      },
      onError: (error) => {
        console.error('[PreviewFrame] Widget render error:', error);
        // Clear container on error
        if (widgetContainerRef.current) {
          widgetContainerRef.current.innerHTML = '';
        }
      }
    }).then((result) => {
      if (result.success && result.widgetTag) {
        setSelectedWidgetTag(result.widgetTag);
      }
    }).catch((error) => {
      console.error('[PreviewFrame] Fatal error in widget rendering:', error);
    });

    // Cleanup function to remove widget on unmount or template change
    return () => {
      if (widgetContainerRef.current) {
        widgetContainerRef.current.innerHTML = '';
      }
      setSelectedWidgetTag(null);
    };
  }, [channel, isWebWidgetTemplate, effectiveTemplateKey, isReady, instanceId, resolvedScenarioId, refreshKey, reinitialize]); // Remove isReinitializing from dependencies - unified function handles it
  
  // Track template, refreshKey, and scenario changes to update refs (for determining when to re-initialize)
  React.useEffect(() => {
    if (effectiveTemplateKey && effectiveTemplateKey !== prevTemplateKeyRef.current) {
      prevTemplateKeyRef.current = effectiveTemplateKey;
    }
    if (refreshKey !== null && refreshKey !== prevRefreshKeyRef.current) {
      prevRefreshKeyRef.current = refreshKey;
    }
    if (resolvedScenarioId && resolvedScenarioId !== prevScenarioIdRef.current) {
      prevScenarioIdRef.current = resolvedScenarioId;
    }
  }, [effectiveTemplateKey, refreshKey, resolvedScenarioId]);

  const subjectLine = renderedSubject || null;

  const displayHtml = React.useMemo(() => {
    // Only for Email teaser: reveal hidden preheader/teaser text so users can see/edit it.
    if (channel !== 'Email') return renderedHtml;
    if (!renderedHtml) return renderedHtml;
    if (effectiveElementKey !== 'TEASER') return renderedHtml;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(renderedHtml, 'text/html');
      const nodes = Array.from(doc.querySelectorAll('tr,td,div,span'));
      let changed = 0;
      for (const el of nodes) {
        const style = el.getAttribute('style') || '';
        const hasHidden =
          style.includes('mso-hide:all') ||
          style.includes('display:none') ||
          style.includes('visibility:hidden') ||
          style.includes('max-height:0') ||
          style.includes('opacity:0');
        if (!hasHidden) continue;

        // Heuristic: only reveal nodes that are meant to be preheader/teaser
        // (they are usually hidden via mso-hide/display:none and contain teaser text).
        const text = (el.textContent || '').trim();
        if (!text) continue;

        const newStyle = [
          // Force visible
          'display:block !important',
          'visibility:visible !important',
          'max-height:none !important',
          'max-width:none !important',
          'opacity:1 !important',
          'overflow:visible !important',
          // Make readable
          'font-size:14px !important',
          'line-height:1.4 !important',
          'color:#111 !important',
          // Add subtle frame so users know it’s normally hidden
          'background:#fffbe6',
          'border:1px dashed #d0d0d0',
          'padding:8px',
          'margin:8px 0',
        ].join('; ');
        el.setAttribute('style', newStyle);
        changed++;
      }

      // Re-serialize
      return doc.documentElement?.outerHTML || renderedHtml;
    } catch {
      return renderedHtml;
    }
  }, [channel, effectiveElementKey, renderedHtml]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
      {!renderRequest && (
        <Box sx={{ padding: 1.5, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">Select a template to preview</Typography>
        </Box>
      )}
      {variantName && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            padding: '6px 12px',
            borderRadius: 1.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: theme => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(0, 0, 0, 0.02)',
            color: 'text.secondary',
            fontSize: '12px',
            fontWeight: 500
          }}
        >
          <Typography component="span" variant="caption">{variantName}:</Typography>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.hierarchy || 'Project'}
          </Box>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.locale || 'en_US'}
          </Box>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.scenario || 'default'}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          borderRadius: 2,
          bgcolor: theme => theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
          padding: 2,
          overflow: 'auto'
        }}
      >
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 5,
              color: 'text.secondary'
            }}
          >
            <Typography variant="body2">Loading preview...</Typography>
          </Box>
        )}

        {!loading && channel === 'Email' && subjectLine && isFullTemplateSelected && (
          <Card
            sx={{
              marginBottom: 2,
              boxShadow: theme => theme.palette.mode === 'dark'
                ? '0 4px 6px rgba(0,0,0,0.3)'
                : '0 4px 6px rgba(0,0,0,0.1)',
              bgcolor: 'background.paper'
            }}
          >
            <CardHeader
              sx={{ padding: 1.5 }}
              title={
                <Box
                  component="label"
                  htmlFor={`subject-line-preview-${variantName || 'main'}`}
                sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'text.secondary',
                  display: 'block',
                  marginBottom: 0.5
                }}
              >
                Subject Line
                </Box>
              }
              subheader={
                <Typography
                  id={`subject-line-preview-${variantName || 'main'}`}
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    margin: 0
                  }}
                >
                {subjectLine}
              </Typography>
              }
            />
          </Card>
        )}

        {/* Web widget path: direct render (no iframe, same as WidgetsSdkDemoPage) */}
        {!loading && channel === 'Web' && isWebWidgetTemplate ? (
          <WidgetErrorBoundary>
            <Box
              sx={{
                margin: '0 auto',
                bgcolor: 'background.paper',
                boxShadow: theme => theme.palette.mode === 'dark'
                  ? '0 4px 6px rgba(0,0,0,0.3)'
                  : '0 4px 6px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                borderRadius: 2,
                minWidth: '1152px',
                minHeight: 400,
                padding: 2
              }}
            >
              {isInitializing && (
                <Box sx={{ padding: 2, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">Initializing widget SDK...</Typography>
                </Box>
              )}
              {initError && (
                <Box sx={{ padding: 2, textAlign: 'center', color: 'error.main' }}>
                  <Typography variant="body2">Error: {initError}</Typography>
                </Box>
              )}
              {!isInitializing && !initError && isReady && (
                <div
                  ref={widgetContainerRef}
                  id="widget-container"
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '400px'
                  }}
                />
              )}
              {!isInitializing && !initError && !isReady && (
                <Box sx={{ padding: 2, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">Waiting for widget SDK...</Typography>
                </Box>
              )}
            </Box>
          </WidgetErrorBoundary>
        ) : !loading && renderedHtml ? (
          // Render HTML from API - apply mobile or desktop styling based on previewMode
          previewMode === 'mobile' && channel !== 'HER' ? (
            // Mobile preview with phone frame
            <Box
              sx={{
                margin: '0 auto',
                width: '360px',
                height: '740px',
                bgcolor: '#000000',
                border: '14px solid #000000',
                borderRadius: '40px',
                boxShadow: theme => theme.palette.mode === 'dark'
                  ? '0 20px 25px rgba(0,0,0,0.6)'
                  : '0 20px 25px rgba(0,0,0,0.4)',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  bgcolor: 'background.paper',
                  overflowY: 'auto'
                }}
              >
                <Box
                  dangerouslySetInnerHTML={{ __html: displayHtml || '' }}
                  sx={{
                    width: '100%',
                    maxWidth: '100%'
                  }}
                />
              </Box>
            </Box>
          ) : (
            // Desktop preview
            <Box
              sx={{
                margin: '0 auto',
                bgcolor: 'background.paper',
                boxShadow: theme => theme.palette.mode === 'dark'
                  ? '0 4px 6px rgba(0,0,0,0.3)'
                  : '0 4px 6px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                borderRadius: 2,
                ...(channel === 'Web' 
                  ? { minWidth: '1152px' }
                  : { maxWidth: '672px' })
              }}
            >
              <Box
                dangerouslySetInnerHTML={{ __html: displayHtml || '' }}
                sx={{
                  width: '100%',
                  maxWidth: '100%'
                }}
              />
            </Box>
          )
        ) : !loading && (
          // Show message if no rendered HTML available
          <Box sx={{ padding: 1.5, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No preview available</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

